import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import { actors } from "@/mocks/actors";
import { articles } from "@/mocks/articles";
import { movies } from "@/mocks/movies";

const tabs = [
  { value: "all", label: "全部" },
  { value: "movie", label: "影片" },
  { value: "actor", label: "演员" },
  { value: "article", label: "文章" },
];

export default function SearchPage() {
  useSeo({
    title: "搜索结果 - 蜜桃成熟时1997影视内容站",
    description:
      "全站搜索影片标题、演员姓名与资讯文章，快速定位你感兴趣的经典电影内容与解读文章。",
    keywords: "搜索,影片搜索,演员搜索,资讯搜索,经典电影",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get("q") || "";
  const [input, setInput] = useState(queryParam);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    setInput(queryParam);
  }, [queryParam]);

  const keyword = queryParam.trim().toLowerCase();

  const movieResults = useMemo(() => {
    if (!keyword) return [];
    return movies.filter(
      (movie) =>
        movie.title.toLowerCase().includes(keyword) ||
        movie.director.toLowerCase().includes(keyword) ||
        movie.cast.some((name) => name.toLowerCase().includes(keyword)) ||
        movie.tags.some((tag) => tag.toLowerCase().includes(keyword)),
    );
  }, [keyword]);

  const actorResults = useMemo(() => {
    if (!keyword) return [];
    return actors.filter(
      (actor) =>
        actor.name.toLowerCase().includes(keyword) || actor.bio.toLowerCase().includes(keyword),
    );
  }, [keyword]);

  const articleResults = useMemo(() => {
    if (!keyword) return [];
    return articles.filter(
      (article) =>
        article.title.toLowerCase().includes(keyword) ||
        article.excerpt.toLowerCase().includes(keyword) ||
        article.tag.toLowerCase().includes(keyword),
    );
  }, [keyword]);

  const totalCount = movieResults.length + actorResults.length + articleResults.length;

  const counts: Record<string, number> = {
    all: totalCount,
    movie: movieResults.length,
    actor: actorResults.length,
    article: articleResults.length,
  };

  const showMovies = tab === "all" || tab === "movie";
  const showActors = tab === "all" || tab === "actor";
  const showArticles = tab === "all" || tab === "article";

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = input.trim();
    setSearchParams(trimmed ? { q: trimmed } : {});
    setTab("all");
  };

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />
      </div>

      <section className="w-full px-4 md:px-8 pt-6 pb-8">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
          全站搜索
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600">
          搜索影片标题、演员姓名与资讯文章，快速找到你想要的内容。
        </p>

        <form onSubmit={submit} className="mt-6 max-w-2xl">
          <div className="flex items-center gap-2 rounded-full pl-4 pr-1.5 py-1.5 border border-background-300 bg-background-50">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line"></i>
            </span>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入影片名称、演员姓名或关键词"
              className="bg-transparent outline-none text-sm flex-1 text-foreground-900 placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </form>

        {keyword && (
          <p className="mt-5 text-sm text-foreground-600">
            关键词「<span className="text-primary-600 font-medium">{queryParam}</span>」，共找到{" "}
            <span className="text-primary-600 font-medium">{totalCount}</span> 条结果
          </p>
        )}
      </section>

      {keyword && (
        <section className="w-full px-4 md:px-8 pb-16">
          <div className="flex flex-wrap gap-2 mb-8">
            {tabs.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setTab(item.value)}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  tab === item.value
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item.label} ({counts[item.value]})
              </button>
            ))}
          </div>

          {totalCount === 0 && (
            <div className="rounded-lg border border-background-200 bg-background-50 py-20 text-center">
              <span className="mx-auto w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                没有找到相关内容
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词试试，或浏览电影分类寻找感兴趣的作品。
              </p>
              <Link
                to="/category"
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-film-line"></i>
                浏览电影分类
              </Link>
            </div>
          )}

          {showMovies && movieResults.length > 0 && (
            <div className="mb-12">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-film-line text-primary-600"></i>
                相关影片
                <span className="text-sm font-normal text-foreground-400">
                  {movieResults.length} 部
                </span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {movieResults.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          )}

          {showActors && actorResults.length > 0 && (
            <div className="mb-12">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-user-star-line text-primary-600"></i>
                相关演员
                <span className="text-sm font-normal text-foreground-400">
                  {actorResults.length} 位
                </span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {actorResults.map((actor) => (
                  <Link
                    key={actor.id}
                    to={`/actors/${actor.id}`}
                    className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors"
                  >
                    <div className="w-full aspect-square overflow-hidden bg-background-200">
                      <img
                        src={actor.avatar}
                        alt={`${actor.name} 演员头像`}
                        title={`${actor.name} - 演员介绍`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {actor.name}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                        {actor.bio}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {showArticles && articleResults.length > 0 && (
            <div>
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-article-line text-primary-600"></i>
                相关文章
                <span className="text-sm font-normal text-foreground-400">
                  {articleResults.length} 篇
                </span>
              </h2>
              <div className="space-y-4">
                {articleResults.map((item) => (
                  <Link
                    key={item.id}
                    to={item.type === "analysis" ? "/features/plot-analysis" : "/guides"}
                    className="group flex flex-col sm:flex-row gap-4 rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors"
                  >
                    <div className="w-full sm:w-52 h-40 sm:h-32 shrink-0 overflow-hidden bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 内容文章`}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0 p-4 sm:py-5 sm:pr-6">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-xs">
                          {item.tag}
                        </span>
                        <span className="text-xs text-foreground-400">{item.date}</span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {item.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {!keyword && (
        <section className="w-full px-4 md:px-8 pb-16">
          <div className="rounded-lg border border-background-200 bg-background-100 p-8 text-center">
            <span className="mx-auto w-14 h-14 rounded-full bg-background-50 text-primary-500 flex items-center justify-center">
              <i className="ri-search-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
              输入关键词开始搜索
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              试试搜索「蜜桃成熟时1997」「重庆森林」「张国荣」等关键词。
            </p>
          </div>
        </section>
      )}
    </div>
  );
}